<i18n>
{
	"en": {
		"description": "Description",
		"header": "Cargo Carriers",
		"icon": "Icon",
		"noCargoCarriersFound": "No cargo carriers found",
		"owner": "Owner",
		"registrationNumber": "Registration number",
		"type": "Type (TMS ID)",
		"palletCapacity": "Pallet Capacity",
		"volume": "Volume (m3)",
		"weight": "Weight (kg)",
		"loadingMeters": "Loading Meters",
		"failedToFetch": "Failed to fetch cargo carriers",
		"faieldToUpdateIcon": "Failed to update icon",
		"failedToUpdatePalletCapacity": "Failed to update pallet capacity",
		"failedToUpdateVolume": "Failed to update volume",
		"failedToUpdateWeight": "Failed to update weight",
		"failedToUpdateLoadingMeters": "Failed to update loading meters",
		"updatedIcon": "Updated icon",
		"updatedPalletCapacity": "Successfully updated pallet capacity",
		"updatedVolume": "Successfully updated volume",
		"updatedWeight": "Successfully updated weight",
		"updatedLoadingMeters": "Successfully updated loading meters"
	},
	"no": {
		"description": "Beskrivelse",
		"header": "Lastebærere",
		"icon": "Ikon",
		"noCargoCarriersFound": "Fant ingen lastebærere",
		"owner": "Eier",
		"registrationNumber": "Registreringsnummer",
		"type": "Type (TMS ID)",
		"palletCapacity": "Pallkapasitet",
		"volume": "Volum (m3)",
		"weight": "Vekt (kg)",
		"loadingMeters": "Lastemeter",
		"failedToFetch": "Kunne ikke hente lastebærere",
		"failedToUpdateIcon": "Kunne ikke oppdatere ikon",
		"failedToUpdatePalletCapacity": "Kunne ikke oppdatere pallkapasitet",
		"failedToUpdateVolume": "Kunne ikke oppdatere volum",
		"failedToUpdateWeight": "Kunne ikke oppdatere vekt",
		"failedToUpdateLoadingMeters": "Kunne ikke oppdatere lastemeter",
		"updatedIcon": "Ikonet ble oppdatert",
		"updatedPalletCapacity": "Successfully updated pallkapasitet",
		"updatedVolume": "Volum  ble oppdatert",
		"updatedWeight": "Vekt ble oppdatert",
		"updatedLoadingMeters": "Lastemeter ble oppdatert"
	}
}
</i18n>

<template>
	<t-vertical-panel class="cargo-carriers">
		<div slot="header" class="cargo-carriers-header">
			<t-panel-header size="md" icon="truck-front">
				{{ $t('header') }}
			</t-panel-header>
		</div>
		<div class="cargo-carriers-content">
			<t-grid
				v-if="cargoCarriers"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="40"
				:items="cargoCarriers"
				localStorageId="cargoCarriersList"
			>
				<template v-slot:cargoCarrierType="{ item }">
					<div
						:style="{
							display: 'flex',
							alignItems: 'center',
						}"
					>
						<t-timpex-icon
							v-if="item.cargoCarrierType && item.cargoCarrierType.iconId"
							:icon="getCargoCarrierIcon(
								item.cargoCarrierType
									&& item.cargoCarrierType.iconId
							)"
							color="neutral"
						/>
						<t-timpex-icon
							v-else
							icon="unknown-vehicle"
							type="bp"
							color="orange"
						/>
						<span style="margin-left: 10px;">
							{{`
							${
								item.cargoCarrierType && item.cargoCarrierType.code
								|| '?'
							} (${
								item.cargoCarrierType && item.cargoCarrierType.r10Id
								|| '?'
							})
							`}}
						</span>
					</div>
				</template>

				<template v-slot:iconId="{ item }">
					<t-select
						:selectedItem="item.iconId"
						:items="cargoCarriersIcons"
						appearance="minimal"
						relative
						disableBoxShadow
						@select="updateCargoCarrierIcon({
							cargoCarrier: item,
							iconId: $event
						})"
					>
						<template v-slot:displayedValue="{popoverShow}">
							<div class="icon-wrapper">
								<t-timpex-icon
									v-if="!item.iconId && !popoverShow"
									icon="unknown-vehicle"
									type="bp"
									color="orange"
								/>
								<t-timpex-icon v-else :icon="getCargoCarrierIcon(item.iconId)" color="neutral" />
							</div>
						</template>
						<template v-slot:optionItem="slotProps">
							<t-menu-combobox-option-item
								:value="slotProps.item"
								:selected="slotProps.isSelected"
							>
								<t-timpex-icon :icon="getCargoCarrierIcon(slotProps.item)" color="neutral" />
							</t-menu-combobox-option-item>
						</template>
					</t-select>
				</template>

				<template v-slot:palletCapacity="{ item }">
					<t-number-input
						min="0"
						:value="item.palletCapacity"
						:isInvalid="errors.palletCapacity && item.id === errors.id"
						@change="updatePalletCapacity({cargoCarrier: item, palletCapacity: tryParseNumber($event)})"
					/>
				</template>

				<template v-slot:volume="{ item }">
					<t-number-input
						min="0"
						:value="item.volume"
						:isInvalid="errors.volume && item.id === errors.id"
						@change="updateVolume({cargoCarrier: item, volume: tryParseNumber($event)})"
					/>
				</template>

				<template v-slot:weight="{ item }">
					<t-number-input
						min="0"
						:value="item.weight"
						:isInvalid="errors.weight && item.id === errors.id"
						@change="updateWeight({cargoCarrier: item, weight: tryParseNumber($event)})"
					/>
				</template>

				<template v-slot:loadingMeters="{ item }">
					<t-number-input
						min="0"
						:value="item.loadingMeters"
						:isInvalid="errors.loadingMeters && item.id === errors.id"
						@change="updateLoadingMeters({cargoCarrier: item, loadingMeters: tryParseNumber($event)})"
					/>
				</template>
			</t-grid>

			<t-no-content-panel v-else :text="$t('noCargoCarriersFound')" icon="truck-front" />

			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
	</t-vertical-panel>
</template>

<script>

import { cargoCarrierIcon } from '@/core';
import { mapState, mapActions } from './store/cargoCarriers.store.js';
import TVerticalPanel from '@/components/evergreen/timpex/layers/TVerticalPanel';
import TPanelHeader from '@/components/evergreen/timpex/layers/TPanelHeader';
import TGrid from '@/components/evergreen/timpex/grid/TGrid';
import TSelect from '@/components/evergreen/inputs/TSelect';
import TTimpexIcon from '@/components/evergreen/timpex/icons/TTimpexIcon';
import TMenuComboboxOptionItem from '@/components/evergreen/menu/TMenuComboboxOptionItem';
import TNumberInput from '@/components/evergreen/timpex/inputs/TNumberInput';
import TNoContentPanel from '@/components/evergreen/timpex/layers/TNoContentPanel';
import TOverlay from '@/components/evergreen/timpex/layers/TOverlay';

export default {
	name: 'CargoCarriers',
	components: { TOverlay, TNoContentPanel, TNumberInput, TMenuComboboxOptionItem, TTimpexIcon, TSelect, TGrid, TPanelHeader, TVerticalPanel },
	data() {
		return {
			defaultColumnDefinition: { resizable: true },
			rowsSelected: 0,
			cargoCarriersIcons: Object.values(cargoCarrierIcon.iconIds),
			selectedCC: 'semi',
		};
	},
	computed: {
		...mapState([
			'cargoCarriers',
			'loading',
			'errors'
		]),
		columnDefinitions() {
			return [
				{
					field: 'cargoCarrierType',
					displayName: this.$t('type'),
					valueGetter: (item) =>
						`${
							item.cargoCarrierType && item.cargoCarrierType.code
							|| '?'
						} (${
							item.cargoCarrierType && item.cargoCarrierType.r10Id
							|| '?'
						})`,
					width: 200,
				},
				{
					field: 'description',
					displayName: this.$t('description'),
					minWidth: 125,
				},
				{
					field: 'registrationNumber',
					displayName: this.$t('registrationNumber'),
					minWidth: 125,
				},
				{
					field: 'vendor',
					displayName: this.$t('owner'),
					valueGetter: (item) => item.vendor ? item.vendor.name : '',
					minWidth: 125,
				},
				{
					field: 'palletCapacity',
					displayName: this.$t('palletCapacity'),
					minWidth: 80,
				},
				{
					field: 'volume',
					displayName: this.$t('volume'),
					minWidth: 80,
				},
				{
					field: 'weight',
					displayName: this.$t('weight'),
					minWidth: 80,
				},
				{
					field: 'loadingMeters',
					displayName: this.$t('loadingMeters'),
					minWidth: 80,
				},
			];
		}
	},
	async created() {
		try {
			await this.getCargoCarriers();
		} catch (e) {
			this.$notify({
				type: 'error',
				duration: -1,
				title: this.$t('failedToFetch')
			});
		}
	},
	beforeDestroy() {
		this.$notify({ clean: true });
	},
	methods: {
		...mapActions([
			'getCargoCarriers',
			'setCargoCarrier',
		]),
		deleteCargoCarriers() {
			// eslint-disable-next-line no-alert
			alert('Delete'); // refactor, eslint rule no-alert
		},
		addCargoCarrier() {
			// eslint-disable-next-line no-alert
			alert('Add'); // refactor, eslint rule no-alert
		},
		tryParseNumber(value) {
			let number = Number(value);
			if (Number.isNaN(number)) return undefined;
			return number;
		},
		async updateCargoCarrierIcon({ cargoCarrier, iconId }) {
			try {
				await this.setCargoCarrier({ cargoCarrier: cargoCarrier, data: iconId, prop: 'iconId' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedIcon'),
					text: cargoCarrier.description
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateIcon'),
					text: cargoCarrier.description
				});
			}
		},
		async updatePalletCapacity({ cargoCarrier, palletCapacity }) {
			try {
				await this.setCargoCarrier({ cargoCarrier: cargoCarrier, data: palletCapacity, prop: 'palletCapacity' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedPalletCapacity'),
					text: cargoCarrier.description
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdatePalletCapacity'),
					text: cargoCarrier.description
				});
			}
		},
		async updateVolume({ cargoCarrier, volume }) {
			try {
				await this.setCargoCarrier({ cargoCarrier: cargoCarrier, data: volume, prop: 'volume' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedVolume'),
					text: cargoCarrier.description
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateVolume'),
					text: cargoCarrier.description
				});
			}
		},
		async updateWeight({ cargoCarrier, weight }) {
			try {
				await this.setCargoCarrier({ cargoCarrier: cargoCarrier, data: weight, prop: 'weight' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedWeight'),
					text: cargoCarrier.description
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateWeight'),
					text: cargoCarrier.description
				});
			}
		},
		async updateLoadingMeters({ cargoCarrier, loadingMeters }) {
			try {
				await this.setCargoCarrier({ cargoCarrier: cargoCarrier, data: loadingMeters, prop: 'loadingMeters' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedLoadingMeters'),
					text: cargoCarrier.description
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateLoadingMeters'),
					text: cargoCarrier.description
				});
			}
		},
		getCargoCarrierIcon(iconId) {
			return cargoCarrierIcon.getIcon(iconId);
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
	},
};
</script>


<style lang="scss" scoped>
.cargo-carriers {
	.cargo-carriers-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.cargo-carriers-sub-header {
		display: flex;
		padding: 0 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.cargo-carriers-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0 5px;
	}
}

.icon-wrapper {
	height: 100%;
	display: flex;
	justify-content: flex-start;
}
</style>
